"use client";

import { Card, LiquidPill, Modal, resolveCorporateValueVisual } from "components/ui/v2";
import RecognitionByCorporateValue from "components/ui/v2/maintainers/bundle/CorporateValue/Recognition/RecognitionByCorporateValue";
import { ERecognitionType } from "types/CorporateValue.type";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";

interface RecognitionValueCardV2Props {
  srcImage: string | null;
  title: string;
  quantity: string | number;
  corporateValueId: string;
  enableSeeValues?: boolean;
  userId?: string;
  type: "received" | "sent";
}

function getInitials(name: string, minWordLength: number) {
  const words = name.split(" ").filter((word) => word.length >= minWordLength);
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}

export function RecognitionValueCardV2({
  srcImage,
  title,
  quantity,
  corporateValueId,
  enableSeeValues = false,
  userId = "",
  type,
}: RecognitionValueCardV2Props) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [displayQuantity, setDisplayQuantity] = useState(Number(quantity) || 0);
  const { variant: cvVariant } = resolveCorporateValueVisual(title);

  useEffect(() => {
    setDisplayQuantity(Number(quantity) || 0);
  }, [quantity]);

  const recognitionType =
    type === "received" ? ERecognitionType.RECEIVING : ERecognitionType.SENDING;

  return (
    <>
      <div
        className={enableSeeValues ? "cursor-pointer transition-all duration-200 hover:shadow-md" : undefined}
        onClick={enableSeeValues ? () => setIsOpen(true) : undefined}
        role={enableSeeValues ? "button" : undefined}
        tabIndex={enableSeeValues ? 0 : undefined}
        onKeyDown={
          enableSeeValues
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsOpen(true);
                }
              }
            : undefined
        }
      >
        <Card size="compact" padding="comfortable">
          <div className="flex flex-col items-center gap-3">
            <div className="flex flex-col items-center gap-2">
              {srcImage ? (
                <picture className="relative h-[50px] w-[50px] overflow-hidden rounded-full">
                  <Image
                    src={srcImage}
                    alt={title}
                    fill
                    className="h-full w-full object-cover"
                  />
                </picture>
              ) : (
                <div className="relative flex h-[50px] w-[50px] items-center justify-center rounded-full bg-slate-200 dark:bg-zinc-700">
                  <span className="text-lg font-bold text-slate-700 dark:text-zinc-200">
                    {getInitials(title, type === "received" ? 3 : 3)}
                  </span>
                </div>
              )}
              <h4 className="text-center text-sm font-semibold text-slate-900 dark:text-white">
                {title}
              </h4>
            </div>
            <div className="flex flex-col items-center gap-2">
              {type === "received" ? (
                <p className="text-xs text-slate-600 dark:text-zinc-400">{t("Recibidos")}</p>
              ) : null}
              <LiquidPill
                variant={cvVariant}
                appearance="tinted"
                shape="rounded"
                size="md"
                text={String(displayQuantity)}
              />
            </div>
          </div>
        </Card>
      </div>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={title}
        size="lg"
        panelClassName="flex max-h-[min(88vh,780px)] flex-col"
        headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
        bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
        bodyScrollFade
      >
        <RecognitionByCorporateValue
          corporateValueId={corporateValueId}
          corporateValueName={title}
          corporateValueIcon={srcImage ?? ""}
          quantity={displayQuantity}
          recognitionType={recognitionType}
          userId={userId}
        />
      </Modal>
    </>
  );
}
